import { langStore, translate, useSelecting } from "@noli/business";
import { shallowCopy } from "@noli/core";

import React, { useMemo, useState } from "react";
import { checkAIMeta, toast, useOwnToast } from "@/utils/misc";
import { chakra, Flex, Text } from "@chakra-ui/react";
import { Editor_Words } from "@/utils/lang/editor";
import { Actions } from "@/utils/api";
import { SubWithTitle } from "@/components/SubPanel/WithTitle";
import { ImageWithPlaceHolder } from "@/components/Image/WithPlaceHolder";
import { observer } from "mobx-react-lite";
import { parseModelType } from "@/modules/editor/utils/meta";
import { MODEL_TYPES_INFO } from "@/modules/editor/constant";
import { InfoText, LabelText } from "@/modules/editor/components/styleComponents/Texts";
import { Fidelity } from "@/modules/editor/components/common/Fidelity";
import useImageWithPlaceholder, {
  checkNoliboxAICondition,
} from "@/modules/editor/hooks/useImageWithPlaceholder";
import { GenMultiInput } from "@/modules/editor/components/common/GenMultiInput";
import { setGenerateCount, statusStore } from "@/stores/status";
import { isAuthUser, isVip } from "@/utils/status";
import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
import { GenerateBizType } from "@/types/creator";
import { GeneratePriceRules } from "@/modules/editor/hooks/useGenerateCost";
import { TooltipInfo } from "@/components/Icons";

/**
 * 相似图片生成
 */
const IImgVariation: React.FC = () => {
  const [fidelity, setFidelity] = useState(0.8);
  const t = useOwnToast();
  const lang = langStore.tgt;

  const info = useSelecting("basic")({ fixed: 0 });
  const { type, displayNode, imgWH } = info ?? {};
  const alias = displayNode?.alias;
  const generateCount = useMemo(() => {
    return isAuthUser() && isVip() ? statusStore.generateCount : 1;
  }, [isVip(), isAuthUser(), statusStore.generateCount]);

  const meta = useMemo(
    () => (displayNode?.type === "group" ? undefined : shallowCopy(displayNode?.meta)),
    [displayNode?.alias, displayNode?.type],
  );

  const btnText = useMemo(() => {
    if (!generateCount || generateCount === 1) {
      const text = translate(Editor_Words["txt2img-sd-variation-submit"], lang);
      return text;
    } else {
      return translate(Editor_Words["txt2img-sd-variation-submit-and-add-template"], lang).replace(
        "$1",
        generateCount.toString(),
      );
    }
  }, [generateCount]);
  const enableVariation = type === "image" && checkAIMeta(meta);

  async function handleVariation(): Promise<void> {
    // sanity check
    if (!alias || displayNode?.type !== "image") {
      return;
    }

    if (!checkAIMeta(meta)) {
      toast(t, "info", translate(Editor_Words["txt2img-sd-variation-not-support-message"], lang));
      return;
    }
    // call api
    await Actions.sdVariation({
      t,
      lang,
      busyAliases: isVip() ? [] : [alias],
      meta,
      fidelity,
      generateCount,
    });
  }

  const modelType = useMemo(() => (meta ? parseModelType(meta) : ""), [meta]);

  const { url, placeholder } = useImageWithPlaceholder({
    condition: checkNoliboxAICondition(),
    placeholderConfig: {
      noneSelect: Editor_Words["select-a-ai-picture"],
      notTargetTypeNode: (
        <TooltipInfo
          title={
            <Text lineHeight="15px">
              {translate(Editor_Words["current_select_not_for_variation"], lang)}
            </Text>
          }
          info={
            <Flex flexDir="column" fontSize="14px" lineHeight="22px" color="white">
              {translate(Editor_Words["variation_usable_tooltip_text"], lang)}
            </Flex>
          }
          tooltipProps={{
            arrowSize: 5,
            maxWidth: "250px",
          }}
        />
      ),
    },
  });

  const sizeString = useMemo(() => {
    return imgWH ? `${imgWH.w} × ${imgWH.h} px` : translate(Editor_Words["general-loading"], lang);
  }, [imgWH]);

  return (
    <SubWithTitle
      flex={1}
      minH="100%"
      title={translate(Editor_Words["txt2img-sd-variation-title"], lang)}
    >
      <LabelText mt="16px">{translate(Editor_Words["img2img-placeholder-title"], lang)}</LabelText>
      <ImageWithPlaceHolder url={url} placeholderText={placeholder} />
      <Fidelity value={fidelity} setValue={setFidelity} />
      {enableVariation ? (
        <InfoText fontSize="14px" mt="16px">
          <chakra.span color="#fff">{translate(Editor_Words["size"], lang)}：</chakra.span>
          {translate(Editor_Words["stay-in-step-with-selected-pic"], lang)}
        </InfoText>
      ) : null}
      {isAuthUser() && (
        <GenMultiInput
          mt="16px"
          value={statusStore.generateCount}
          onChange={setGenerateCount}
          min={1}
          max={100}
        />
      )}
      {enableVariation ? (
        <Flex flexDirection="column" gap="12px" mt="16px">
          <InfoText>
            {translate(Editor_Words["model"], lang)}：
            {modelType ? translate(MODEL_TYPES_INFO[modelType]?.showInfo, lang) : ""}
          </InfoText>
          <InfoText>
            {translate(Editor_Words["resolution"], lang)}： {sizeString}
          </InfoText>
        </Flex>
      ) : null}
      <BizGenerateButton
        sticky
        mt="48px"
        size={imgWH}
        generateCount={generateCount}
        calcPriceProps={GeneratePriceRules[GenerateBizType.Variation]}
        checkBusyConfig={{
          busyAlias: alias,
          checkVip: true,
        }}
        buttonProps={{
          isDisabled: !enableVariation,
        }}
        onSubmit={handleVariation}
      >
        {btnText}
      </BizGenerateButton>
    </SubWithTitle>
  );
};

export const Txt2ImgVariation = observer(IImgVariation);
